import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { FC, useEffect, useRef } from "react";
import { DataGrid, GridColDef, GridRow } from "@mui/x-data-grid";
import { useToast } from "../hooks/use-toast.hook";
import { Skeleton } from "@mui/material";
import EmptyStateCard from "./empty-state-card.component";
import BlockIcon from "@mui/icons-material/Block";

const DEFAULT_TABLE_ITEMS_COUNT = 10;

const CustomGridRow = ({ row, fetchNextPage, loaderHeight, ...rest }: any) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!row.isObserverRow || !ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      },
      {
        root: document.querySelector(".MuiDataGrid-virtualScroller") || null,
        threshold: 1.0,
      },
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [row.isObserverRow, fetchNextPage]);

  if (row.isObserverRow) {
    return (
      <div ref={ref}>
        <Skeleton
          variant="rounded"
          width="100%"
          height={loaderHeight}
          style={{ marginBottom: 10 }}
        />{" "}
        <Skeleton
          variant="rounded"
          width="100%"
          height={loaderHeight}
          style={{ marginBottom: 10 }}
        />{" "}
        <Skeleton
          variant="rounded"
          width="100%"
          height={loaderHeight}
          style={{ marginBottom: 10 }}
        />
      </div>
    );
  }

  return <GridRow row={row} {...rest} data-test-id="test-row" />;
};

const TableLoading = ({ loaderHeight }: { loaderHeight: number }) => {
  return (
    <div>
      <Skeleton
        variant="rounded"
        width="100%"
        height={loaderHeight}
        style={{ marginBottom: 10 }}
      />{" "}
      <Skeleton
        variant="rounded"
        width="100%"
        height={loaderHeight}
        style={{ marginBottom: 10 }}
      />{" "}
      <Skeleton
        variant="rounded"
        width="100%"
        height={loaderHeight}
        style={{ marginBottom: 10 }}
      />
    </div>
  );
};

interface TableComponentProps {
  columns: GridColDef[];
  filters: { [key: string]: any };
  checkboxSelection?: boolean;
  disableColumnMenu?: boolean;
  disableColumnSelector?: boolean;
  disableColumnFilter?: boolean;
  onRowClick?: (row: any) => void;
  getRowId?: (row: any) => string;
  height?: number | string;
  className?: string;
  refresh?: any;
  loaderItemHeight?: number;
  listMethod: any;
}

const TableComponent: FC<TableComponentProps> = ({
  listMethod,
  columns,
  filters,
  disableColumnMenu,
  disableColumnSelector = true,
  disableColumnFilter,
  checkboxSelection,
  onRowClick,
  height,
  getRowId,
  className,
  refresh = "",
  loaderItemHeight = 104,
}) => {
  const { showToast } = useToast();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    refetch,
    error,
  } = useInfiniteQuery({
    queryKey: [`table${listMethod}`, filters],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await listMethod({
        ...filters,
        take: DEFAULT_TABLE_ITEMS_COUNT,
        skip: pageParam * DEFAULT_TABLE_ITEMS_COUNT,
      });

      return {
        rows: response[0],
        total: response[1],
        nextPage:
          response[0].length === DEFAULT_TABLE_ITEMS_COUNT
            ? pageParam + 1
            : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
  });

  const items = data?.pages.flatMap((p) => p.rows) ?? [];
  const totalCount = data?.pages[0]?.total ?? 0;

  useMutation({
    onError: (error: Error) => {
      showToast(error.message, "error");
    },
  });

  const doRefresh = async () => {
    await refetch();
  };

  useEffect(() => {
    if (refresh) doRefresh().then();
  }, [refresh]);

  const NoItemsText = () => {
    const noItemsText =
      filters?.searchString && filters?.searchString.length > 0
        ? `Nothing found with name like ${filters.searchString}`
        : `Nothing yet...`;
    return <EmptyStateCard message={noItemsText} Icon={BlockIcon} />;
  };

  if (error) {
    return <div>Error: {(error as Error).message}</div>;
  }

  const rowsWithObserver =
    hasNextPage && !isFetchingNextPage
      ? [...items, { id: "observer-row", isObserverRow: true }]
      : items;

  return (
    <DataGrid
      data-test-id="test-table"
      className={className}
      rowCount={totalCount}
      loading={isLoading}
      columnHeaderHeight={0}
      rows={rowsWithObserver}
      getRowHeight={() => "auto"}
      columns={columns}
      getRowSpacing={() => ({ top: 0, bottom: 10 })}
      paginationMode="server"
      disableDensitySelector
      disableRowSelectionOnClick
      disableColumnMenu={disableColumnMenu}
      disableColumnSelector={disableColumnSelector}
      disableColumnFilter={disableColumnFilter}
      checkboxSelection={checkboxSelection}
      filterMode="server"
      sortingMode="server"
      slots={{
        noRowsOverlay: NoItemsText,
        loadingOverlay: () => <TableLoading loaderHeight={loaderItemHeight} />,
        row: (props) => (
          <CustomGridRow
            {...props}
            fetchNextPage={fetchNextPage}
            loaderHeight={loaderItemHeight}
          />
        ),
      }}
      hideFooterSelectedRowCount
      showColumnVerticalBorder={false}
      showCellVerticalBorder={false}
      onRowClick={(row) => {
        if (onRowClick) {
          onRowClick(row);
        }
      }}
      hideFooter={true}
      hideFooterPagination
      disableColumnSorting
      // hideFooter={!items.length}
      getRowId={getRowId}
      sx={{
        height,
        width: "100%",
        "& .MuiDataGrid-overlayWrapper": {
          height: "200px !important",
          position: "absolute",
          top: 0,
          left: 0,
        },
        "& .MuiDataGrid-row.Mui-selected": {
          backgroundColor: "inherit !important",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "inherit",
        },
        "& .MuiDataGrid-row.Mui-selected:hover": {
          backgroundColor: "inherit !important",
        },
        "& .MuiDataGrid-cell:focus": {
          outline: "none !important",
        },
        "& .MuiDataGrid-virtualScrollerRenderZone": {
          overflowX: "hidden",
          width: "100%",
        },
        "& .MuiDataGrid-topContainer": {
          display: "none",
        },
        "& .MuiDataGrid-filler > div": {
          border: "none",
        },
      }}
    />
  );
};

export default TableComponent;
